import type { ComputedPlacement, BasePlacement, Instance } from '@popperjs/core'
import { createPopper } from '@popperjs/core'
import clsx from 'clsx'
import React, {
  useState,
  useRef,
  Children,
  useCallback,
  useEffect,
  isValidElement,
  cloneElement,
} from 'react'
import type { PropsWithChildren, ReactElement } from 'react'
import { useId } from '../hooks/useId'
import useIsMounted from '../hooks/useIsMounted'
import { applyMaxSize, maxSize } from '../utilities/applyMaxSize'
import { getInteractiveChild } from '../utilities/getInteractiveChild'
import { Keys } from '../utilities/keyboard'

export interface TooltipProps {
  arrow?: boolean
  title: string
  placement?: ComputedPlacement
}

type ArrowStyles = {
  class: { [key in BasePlacement]: string }
  style: {
    [key in BasePlacement]: {
      borderLeftColor?: string
      borderRightColor?: string
      borderTopColor?: string
      borderBottomColor?: string
    }
  }
}

export interface InteractiveChildNode extends ChildNode {
  // Optional as not all interactive elements support a 'disabled' attribute
  disabled?: boolean
}

const styles: ArrowStyles = {
  class: {
    top: 'top-0 -mt-3 transform -translate-y-full',
    bottom: 'bottom-0 -mb-3 transform translate-y-full',
    left: 'left-0 -ml-3 -mb-1 transform -translate-x-full',
    right: 'right-0 -mr-3 -mb-1 transform translate-x-full',
  },
  style: {
    top: {
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent',
      borderTopColor: 'transparent',
    },
    bottom: {
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent',
      borderBottomColor: 'transparent',
    },
    right: {
      borderRightColor: 'transparent',
      borderBottomColor: 'transparent',
      borderTopColor: 'transparent',
    },
    left: {
      borderBottomColor: 'transparent',
      borderLeftColor: 'transparent',
      borderTopColor: 'transparent',
    },
  },
}

function Tooltip({
  title,
  children,
  arrow = false,
  placement = 'bottom',
}: PropsWithChildren<TooltipProps>): JSX.Element {
  const isMounted = useIsMounted()
  const [popper, setPopper] = useState<Instance | null>(null)
  const [timeoutId, setTimeoutId] = useState<number | null>(null)
  const [arrowDirection, setArrowDirection] = useState<BasePlacement>('top')
  const [show, setShow] = useState(false)
  const [interactive, setInteractive] = useState<
    InteractiveChildNode | undefined
  >()

  const isTouch = window.matchMedia(
    '(any-hover: none) and (any-pointer: coarse)',
  ).matches

  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const arrowRef = useRef<HTMLDivElement | null>(null)
  const hoverableRef = useRef<HTMLDivElement | null>(null)

  const id = `coconut-tooltip-${useId()}`

  useEffect(() => {
    if (!children) {
      return
    }

    setInteractive(getInteractiveChild(hoverableRef.current))
  }, [children])

  const open = () => {
    if (timeoutId) {
      window.clearTimeout(timeoutId)
    }

    hoverableRef.current?.addEventListener('keydown', closeOnEscape)

    if (hoverableRef.current && tooltipRef.current && !popper) {
      const instance = createPopper(hoverableRef.current, tooltipRef.current, {
        placement: placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'flip',
            options: {
              padding: 10,
            },
          },
          {
            name: 'arrow',
            enabled: arrow,
            options: {
              element: arrowRef.current,
              padding: 10,
            },
          },
          {
            name: 'flipArrow',
            enabled: arrow,
            phase: 'write',
            fn({ state }) {
              const popperPlacement = state.placement
              switch (popperPlacement) {
                case 'top':
                case 'top-start':
                case 'top-end':
                  setArrowDirection('bottom')
                  break
                case 'right':
                case 'right-start':
                case 'right-end':
                  setArrowDirection('left')
                  break
                case 'left':
                case 'left-start':
                case 'left-end':
                  setArrowDirection('right')
                  break
                default:
                  setArrowDirection('top')
              }
            },
          },
          maxSize,
          applyMaxSize,
        ],
      })

      setPopper(instance)
    }

    setShow(true)
  }

  const close = useCallback(() => {
    const id = window.setTimeout(() => {
      if (isMounted.current) {
        setShow(false)
        popper?.destroy()
        setPopper(null)
      }
    }, 125)

    setTimeoutId(id)
  }, [isMounted, popper])

  const closeOnEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === Keys.Escape) {
        return close()
      }
    },
    [close],
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      closeOnEscape(e)
    },
    [closeOnEscape],
  )

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown])

  if (title.length === 0) {
    return children as JSX.Element
  }

  return (
    <>
      <div
        className={clsx(interactive?.disabled ? 'pointer-events-none' : '')}
        onBlur={close}
        onFocus={open}
        onMouseEnter={open}
        onMouseLeave={close}
        ref={hoverableRef}
      >
        {/**
         * Attach the aria-describedBy to child element so it can be
         * associated to the accessible label and read out by screen
         * readers.
         * Proper tooltip structure should have multiple children,
         * but if that occurs, each child would have the same association
         * which is not a violation.
         */}
        {Children.map(children, (child, i) => {
          if (isValidElement(child) && i === 0) {
            return cloneElement(child as ReactElement, {
              'aria-describedby': id,
            })
          }

          return child
        })}
      </div>
      {isTouch ? null : (
        <div
          className={clsx(
            'px-2.5 py-2 rounded text-white-100 bg-black-60 z-50',
            show ? null : 'hidden',
          )}
          id={id}
          onBlur={close}
          onFocus={open}
          onMouseOut={close}
          onMouseOver={open}
          ref={tooltipRef}
          role="tooltip"
        >
          <div className="max-w-xs font-sans text-sm leading-tight">
            {title}
          </div>
          {arrow ? (
            <div
              className={clsx(
                'border-6 border-black-60 w-0 h-0',
                styles.class[arrowDirection],
              )}
              ref={arrowRef}
              style={styles.style[arrowDirection]}
            />
          ) : null}
        </div>
      )}
    </>
  )
}

export default Tooltip
